import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AuthShell } from "@/components/CarScene";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", remember: false });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data);
      if (data.role === "super_admin") navigate("/super-admin");
      else if (data.role === "institution_admin") navigate("/admin");
      else navigate(data.verification_status === "verified" ? "/home" : "/verify");
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/home";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell tagline="Your campus. Your people. Your ride. Log back in and get moving.">
      <h1 className="font-display font-black text-3xl">Welcome back</h1>
      <p className="text-sm text-white/50 mt-1">Log in to your campus network.</p>
      <form onSubmit={submit} className="bg-[#121417] border border-white/10 rounded-2xl p-6 mt-6 space-y-4 shadow-[0_16px_48px_-16px_rgba(0,0,0,0.8)]">
        <Input data-testid="login-email-input" type="email" required placeholder="Email"
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="rounded-2xl bg-white/5 border-white/10 text-white placeholder:text-white/40" />
        <Input data-testid="login-password-input" type="password" required placeholder="Password"
          value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="rounded-2xl bg-white/5 border-white/10 text-white placeholder:text-white/40" />
        <div className="flex items-center justify-between text-xs">
          <label className="flex items-center gap-2 text-white/60 cursor-pointer">
            <input data-testid="remember-me-checkbox" type="checkbox" checked={form.remember}
              onChange={(e) => setForm({ ...form, remember: e.target.checked })}
              className="w-4 h-4 rounded accent-[#22C55E] bg-white/5" />
            Remember me for 30 days
          </label>
          <Link to="/forgot-password" data-testid="forgot-password-link" className="font-bold text-[#22C55E] hover:text-[#4ADE80] transition-colors duration-200">
            Forgot password?
          </Link>
        </div>
        <Button data-testid="login-submit-btn" disabled={busy} type="submit"
          className="w-full rounded-full font-bold bg-[#0E1A13] text-[#22C55E] border border-[#1DAF5A]/50 hover:bg-[#12241A] shadow-[0_8px_32px_hsla(145,72%,40%,0.25)] active:scale-95 transition-all duration-150">
          {busy ? "Logging in…" : "Log in"}
        </Button>
        <div className="flex items-center gap-3 text-xs text-white/40"><span className="flex-1 h-px bg-white/10" />or<span className="flex-1 h-px bg-white/10" /></div>
        <Button data-testid="login-google-btn" type="button" variant="outline" onClick={googleLogin}
          className="w-full rounded-full font-bold bg-transparent border-white/15 text-white hover:bg-white/10 hover:text-white active:scale-95 transition-all duration-150">
          Continue with Google
        </Button>
      </form>
      <p className="text-sm text-center mt-4 text-white/50">
        New to THIRI? <Link to="/register" data-testid="login-to-register" className="font-bold text-[#22C55E]">Create account</Link>
      </p>
    </AuthShell>
  );
}
